import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  List, ListItemButton, ListItemText, Box, Typography, CircularProgress,
  Chip, Alert,
} from '@mui/material';
import debounce from 'lodash/debounce';
import supplierService from '../../../../services/supplierService';
import venueSupplierService from '../../../../services/venueSupplierService';
import type { Supplier } from '../../../../types/venue';

/**
 * SUPPLIER-CONNECT.1 — grant a venue access to a supplier that already exists.
 *
 * Suppliers are a single global catalogue. Which of them a venue can SEE was
 * derived only from having already transacted with one, so a venue user could
 * not buy from a supplier they could not see, and could not see one they had
 * not bought from. This is the way out of that, and it is deliberately here
 * rather than in the receiving flow: granting access to a shared catalogue is
 * an administrative act, not part of booking in a delivery.
 *
 * Searches the GLOBAL list, which is why the whole dialog is admin-only at the
 * call site — a venue user must never get a browsable directory.
 */

interface Props {
  open: boolean;
  venueId: string;
  /** Suppliers this venue can already see, so they can be marked rather than offered. */
  connectedIds: Set<string>;
  onClose: () => void;
  /** Called after a successful connect so the caller can refresh. */
  onConnected: (supplier: Supplier) => void;
}

const ConnectSupplierDialog: React.FC<Props> = ({
  open, venueId, connectedIds, onClose, onConnected,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Server-side search. The global list is the whole catalogue across every
  // client, so it is never loaded wholesale — only what was asked for.
  const runSearch = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await supplierService.getAllSuppliers(1, 25, 'name', 'asc', q || undefined);
      setResults(res.data ?? []);
    } catch {
      setError('Could not search suppliers. Try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(() => debounce(runSearch, 300), [runSearch]);

  useEffect(() => {
    if (!open) return;
    debouncedSearch(query);
    return () => { debouncedSearch.cancel(); };
  }, [open, query, debouncedSearch]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setError(null); }
  }, [open]);

  const handleConnect = async (supplier: Supplier) => {
    setConnectingId(supplier._id);
    setError(null);
    try {
      await venueSupplierService.connectSupplier(venueId, supplier._id);
      onConnected(supplier);
    } catch {
      setError(`Could not connect ${supplier.name}. Try again.`);
    } finally {
      setConnectingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect a supplier</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search every supplier in the system. Connecting one lets this venue
          raise orders and receive stock against it — it does not move any stock
          or create an order.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {query
                ? `No supplier matches “${query}”.`
                : 'Start typing to search.'}
            </Typography>
            {query && (
              // The one case this dialog cannot solve: the supplier is not in
              // the catalogue at all. Say where to go rather than leaving a
              // dead end — creating a global supplier is a different screen.
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                If they are new to the system, add them under Suppliers first.
              </Typography>
            )}
          </Box>
        ) : (
          <List dense disablePadding>
            {results.map(s => {
              const already = connectedIds.has(s._id);
              return (
                <ListItemButton
                  key={s._id}
                  disabled={already || connectingId !== null}
                  onClick={() => handleConnect(s)}
                >
                  <ListItemText
                    primary={s.name}
                    secondary={[s.type?.replace('_', ' '), s.country].filter(Boolean).join(' · ') || undefined}
                  />
                  {already
                    ? <Chip size="small" label="Connected" />
                    : connectingId === s._id
                      ? <CircularProgress size={18} />
                      : <Button size="small">Connect</Button>}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectSupplierDialog;
